import { useEffect, useState } from "react";

import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { Textarea } from "components/ui/Textarea";
import {
  buildRecallStateRequest,
  MAX_NOTE_LENGTH,
  NOTIFICATION_METHODS,
  RECALL_STATE,
  RECALL_STATE_OPTIONS,
  recallStateError,
} from "features/immunizations/utils/recallState";

import s from "./VaccineRecallTable.module.css";

const toIsoDate = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const methodOptions = NOTIFICATION_METHODS.map((method) => ({
  value: method,
  label: method,
}));

/**
 * What happened when this family was chased about one course (KIN-1890).
 *
 * One course, not the whole patient: a parent contacted about MMR has not been contacted
 * about the 6-in-1, and the recall row already lists the outstanding courses separately.
 *
 * Nothing here is clinical truth — it is workflow state. There is deliberately no
 * "completed": when the dose is recorded the slot becomes GIVEN and the child leaves the
 * list without anyone marking anything.
 */
export const RecordRecallContactDialog = ({
  open,
  patientName,
  courseLabel,
  existingState,
  onClose,
  onSave,
  onClear,
  isSaving,
}) => {
  const [state, setState] = useState(RECALL_STATE.notified);
  const [notificationMethod, setNotificationMethod] = useState("");
  const [snoozeUntil, setSnoozeUntil] = useState("");
  const [note, setNote] = useState("");
  const [hasTriedToSave, setHasTriedToSave] = useState(false);

  // Reopened from what is already recorded, so a correction is an edit rather than a
  // re-entry. A fresh course starts on Contacted, which is the overwhelmingly common case.
  useEffect(() => {
    if (!open) return;

    setState(existingState?.state ?? RECALL_STATE.notified);
    setNotificationMethod(existingState?.notificationMethod ?? "");
    setSnoozeUntil(existingState?.snoozeUntil ?? "");
    setNote(existingState?.note ?? "");
    setHasTriedToSave(false);
  }, [open, existingState]);

  const isSnooze = state === RECALL_STATE.snoozed;
  const error = recallStateError({ state, notificationMethod, snoozeUntil, note });

  const handleSave = () => {
    setHasTriedToSave(true);

    if (error) return;

    onSave(buildRecallStateRequest({ state, notificationMethod, snoozeUntil, note }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Record a recall contact"
      width={520}
    >
      <div className={s.dialogBody}>
        <p className={s.dialogSubject}>
          {patientName} · {courseLabel}
        </p>

        <div className={s.dialogField}>
          <label className={s.dialogLabel} htmlFor="recall-state">
            What happened
          </label>
          <Select
            name="recall-state"
            options={RECALL_STATE_OPTIONS}
            value={state}
            onChange={setState}
            fullWidth
          />
          <small className={s.dialogHelp}>
            {
              RECALL_STATE_OPTIONS.find((option) => option.value === state)
                ?.description
            }
          </small>
        </div>

        {/* Only for a contact. A method on "cannot reach them" would describe a
            conversation that did not happen. */}
        {state === RECALL_STATE.notified && (
          <div className={s.dialogField}>
            <label className={s.dialogLabel} htmlFor="recall-method">
              How (optional)
            </label>
            <Select
              name="recall-method"
              options={methodOptions}
              value={notificationMethod}
              onChange={setNotificationMethod}
              placeholder="Select"
              fullWidth
            />
          </div>
        )}

        {/* Mandatory for a snooze: it is what the course is hidden until, and the backend
            refuses without it. */}
        {!!isSnooze && (
          <div className={s.dialogField}>
            <label className={s.dialogLabel}>Call back after</label>
            <CustomCalendar
              value={snoozeUntil ? new Date(`${snoozeUntil}T00:00:00`) : null}
              onChange={(date) => setSnoozeUntil(toIsoDate(date))}
              placeholder="Select date"
              minDate={new Date()}
            />
          </div>
        )}

        <div className={s.dialogField}>
          <label className={s.dialogLabel} htmlFor="recall-note">
            Note (optional)
          </label>
          <Textarea
            id="recall-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={MAX_NOTE_LENGTH}
            rows={3}
            placeholder="Anything the next person picking this up should know"
          />
        </div>

        {!!(hasTriedToSave && error) && (
          <p className={s.dialogError}>{error}</p>
        )}

        <div className={s.dialogActions}>
          {/* An undo for a state set by mistake, not a way to finish a course. The
              backend soft-deletes, so the row can be marked again afterwards. */}
          {!!existingState && (
            <Button variant="text" size="medium" onClick={onClear} disabled={isSaving}>
              Clear recall state
            </Button>
          )}
          <div className={s.dialogActionsRight}>
            <Button variant="outline" size="medium" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" size="medium" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
